import React, { useCallback, useEffect, useRef, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { REPORT_TYPES } from "../../../conts/simulationConstants";
import "./ReportTypePicker.css";

const ReportTypePicker = ({ value, onChange }) => {
  const trackRef = useRef(null);
  const [edge, setEdge] = useState({ left: false, right: false });

  /* which sides still have hidden cards (drives arrows + edge fades) */
  const updateEdges = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setEdge({
      left: el.scrollLeft > 4,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    updateEdges();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateEdges);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateEdges]);

  const scrollPage = (dir) =>
    trackRef.current?.scrollBy({
      left: dir * trackRef.current.clientWidth * 0.75,
      behavior: "smooth",
    });

  const reveal = (id) =>
    trackRef.current
      ?.querySelector(`[data-id="${id}"]`)
      ?.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" });

  const select = (id) => {
    onChange(id);
    reveal(id);
  };

  /* radiogroup keyboard support: ← → move the selection */
  const handleKeyDown = (e) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const n = REPORT_TYPES.length;
    const i = REPORT_TYPES.findIndex((r) => r.id === value);
    const next = REPORT_TYPES[(i + (e.key === "ArrowRight" ? 1 : -1) + n) % n];
    select(next.id);
    trackRef.current?.querySelector(`[data-id="${next.id}"]`)?.focus();
  };

  return (
    <section className="rt" aria-label="Type de rapport">
      <header className="rt__head">
        <h3>Type de rapport</h3>
        <div className="rt__arrows">
          <button
            type="button"
            className="rt__arrow"
            aria-label="Types précédents"
            disabled={!edge.left}
            onClick={() => scrollPage(-1)}
          >
            <ChevronLeftIcon fontSize="small" />
          </button>
          <button
            type="button"
            className="rt__arrow"
            aria-label="Types suivants"
            disabled={!edge.right}
            onClick={() => scrollPage(1)}
          >
            <ChevronRightIcon fontSize="small" />
          </button>
        </div>
      </header>

      <div
        ref={trackRef}
        role="radiogroup"
        aria-label="Type de rapport"
        className={`rt__track ${edge.left ? "has-left" : ""} ${edge.right ? "has-right" : ""}`}
        onScroll={updateEdges}
        onKeyDown={handleKeyDown}
      >
        {REPORT_TYPES.map(({ id, label, hint }) => (
          <button
            key={id}
            data-id={id}
            type="button"
            role="radio"
            aria-checked={value === id}
            tabIndex={value === id ? 0 : -1}
            className={`rt__item ${value === id ? "is-selected" : ""}`}
            title={`${label} · ${hint}`}
            onClick={() => select(id)}
          >
            <b>{label}</b>
            <small>{hint}</small>
          </button>
        ))}
      </div>
    </section>
  );
};

export default ReportTypePicker;